/**
 * What the selected cells say about the file as a whole.
 *
 * The question a spreadsheet user asks by clicking a cell and glancing at the
 * status bar: is this value one of many, or one of a few? Answered over the
 * whole file rather than the page on screen — a hundred rows out of millions
 * would make the number meaningless.
 */
import { Badge, Button, Group, Loader, Paper, Text, Tooltip } from '@mantine/core';
import { IconFilter, IconX } from '@tabler/icons-react';
import { useSheetCounts, type SheetCell, type SheetFilter } from '../api/queries';
import { TOKENS, formatNumber } from '../theme';

/** A share of the file, phrased so small numbers do not all read as "0%". */
function share(rows: number, total: number): string {
  if (total <= 0 || rows <= 0) return '';
  const pct = (rows / total) * 100;
  if (pct >= 10) return `${pct.toFixed(0)}%`;
  if (pct >= 1) return `${pct.toFixed(1)}%`;
  return '<1%';
}

export function SheetSelectionBar({
  sheetId,
  cells,
  selectedCount,
  onClear,
  onFilter,
}: {
  sheetId: string | null;
  /** The settled selection, which is what gets counted. */
  cells: SheetCell[];
  /** The live selection, so the bar appears on the first click rather than
      after the pause the counting waits for. */
  selectedCount: number;
  onClear: () => void;
  onFilter: (filters: SheetFilter[]) => void;
}) {
  const counts = useSheetCounts(sheetId, cells);
  if (selectedCount === 0) return null;

  const d = counts.data;
  return (
    <Paper withBorder p={6} radius="sm">
      <Group gap="sm" wrap="wrap">
        <Text size="xs" c="dimmed">
          {selectedCount === 1 ? 'Selected value' : `${selectedCount} selected cells`}
        </Text>

        {(counts.isPending || cells.length === 0) && <Loader size="xs" />}
        {counts.error && <Text size="xs" c={TOKENS.redText}>{counts.error.message}</Text>}

        {/*
          Optional chained through the list as well as the response.

          The client logs a mismatch and renders anyway, on purpose, so every
          component reading a response has to survive a field that is not
          there. This one did not: a `counts` the server had not sent took the
          whole grid down with it rather than showing one bar short.
        */}
        {d?.counts?.map((c) => (
          <Badge
            key={`${c.col} ${c.value}`}
            size="sm"
            variant="light"
            color="gray"
            style={{ textTransform: 'none' }}
          >
            {c.col} = {c.value === '' ? '(empty)' : c.value} · {formatNumber(c.rows)} rows{' '}
            {share(c.rows, d.total)}
          </Badge>
        ))}

        {/* Only when it says something the individual counts do not. */}
        {d?.combined !== undefined && cells.length > 1 && (
          <Badge size="sm" variant="filled" color="blue" style={{ textTransform: 'none' }}>
            all together · {formatNumber(d.combined)} rows {share(d.combined, d.total)}
          </Badge>
        )}

        <Group gap={4} ml="auto" wrap="nowrap">
          <Tooltip label="Show only the rows that match">
            <Button
              size="compact-xs"
              variant="subtle"
              leftSection={<IconFilter size={12} />}
              onClick={() => onFilter(cells.map((c) => ({ col: c.col, op: 'eq', value: c.value })))}
            >
              Filter to these
            </Button>
          </Tooltip>
          <Button
            size="compact-xs"
            variant="subtle"
            color="gray"
            leftSection={<IconX size={12} />}
            onClick={onClear}
          >
            Clear
          </Button>
        </Group>
      </Group>
    </Paper>
  );
}
